import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Clock, Hash, Gauge, CheckCircle2, ListChecks, XCircle, Megaphone, ChevronRight } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import KpiTile from '../components/KpiTile.jsx';
import UpcomingCalls from '../components/UpcomingCalls.jsx';
import ScheduledCalls from '../components/ScheduledCalls.jsx';
import LiveCalls from '../components/LiveCalls.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { overviewAPI, tasksAPI, campaignsAPI } from '../services/api.js';
import { AutoPauseChip } from '../components/AutoPauseBanner.jsx';
import useRevalidate from '../hooks/useRevalidate.js';
import { useEventStream } from '../hooks/useEventStream.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// Campaign statuses considered "in flight" — surfaced on the Overview so the owner
// can jump straight to a running campaign to pause/stop/update it. Active first.
const RUNNING_STATUSES = ['active', 'paused'];
const STATUS_BADGE = {
  active: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
  paused: 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
};

// Default zeros so the page renders something sensible even if /overview
// hasn't been implemented in core/ yet.
const EMPTY_METRICS = {
  totalCalls: 0,
  totalMinutes: 0,
  activePhoneNumbers: 0,
  planUtilizationPct: 0,
  planMinutesLimit: null
};

// ── Executive-Admin overview ─────────────────────────────────────────────────
// The chief-of-staff landing: lead with the assistant's WORK (tasks completed /
// pending / not-completed) then a running list of the inbound calls to the line.
// NOT the campaign KPIs (total calls / minutes / plan) — that's the wrong lens for
// a personal assistant. Uses shownTenant so the owner switcher labels it correctly.
function ExecAdminOverview({ shownTenant }) {
  const [counts, setCounts] = useState({ completed: 0, pending: 0, notCompleted: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const t = await tasksAPI.list().catch((e) => { if (e.status === 404) return null; throw e; });
      if (t?.counts) setCounts(t.counts);
    } catch (err) {
      setError(err.message || 'Failed to load your assistant overview.');
    } finally {
      setLoading(false);
    }
  };

  // Key on the viewed tenant so switching tenants (owner switcher, no remount)
  // refetches this tenant's task counts instead of showing the prior tenant's.
  useEffect(() => { load(); }, [shownTenant?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEventStream({ call_ended: () => load() });

  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl">
      <PageHeader
        title="Overview"
        subtitle={shownTenant?.companyName ? `${shownTenant.companyName} · your assistant` : 'Your assistant'}
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiTile
          icon={CheckCircle2}
          label="Tasks completed"
          value={loading ? '…' : (counts.completed ?? 0).toLocaleString()}
        />
        <KpiTile
          icon={ListChecks}
          label="Tasks pending"
          value={loading ? '…' : (counts.pending ?? 0).toLocaleString()}
        />
        <KpiTile
          icon={XCircle}
          label="Not completed"
          value={loading ? '…' : (counts.notCompleted ?? 0).toLocaleString()}
          hint="Tried, but couldn't finish"
        />
      </div>
    </div>
  );
}

// ── Standard (campaign/business) overview — unchanged behavior ───────────────
function StandardOverview({ tenant }) {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [running, setRunning] = useState([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await overviewAPI.metrics();
      // Accept either {data: {...}} or a flat object — be lenient with the
      // backend response shape until core/ stabilises.
      setMetrics({ ...EMPTY_METRICS, ...(data?.data || data || {}) });
    } catch (err) {
      if (err.status === 404) {
        setError(
          'Overview endpoint not yet available in the API. Showing placeholder values.'
        );
      } else {
        setError(err.message || 'Failed to load overview metrics.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Running campaigns (active/paused) for the quick-jump panel. Best-effort — a
  // failure here never blocks the KPI tiles.
  const loadRunning = async () => {
    try {
      const r = await campaignsAPI.list();
      const list = r?.data || r || [];
      setRunning(
        list
          .filter((c) => RUNNING_STATUSES.includes(c.status) && !c.archived)
          .sort((a, b) => RUNNING_STATUSES.indexOf(a.status) - RUNNING_STATUSES.indexOf(b.status)
            || (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
      );
    } catch {
      setRunning([]);
    }
  };

  // Reload BOTH when the viewed tenant changes — the owner switcher swaps tenants
  // without remounting, so keying on tenant.id keeps the numbers + campaigns in
  // sync with the tenant shown (was: [] → stale after a switch).
  useEffect(() => { load(); loadRunning(); }, [tenant?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEventStream({ call_ended: () => load(), campaign_updated: () => loadRunning() });

  // ⚠️ useEventStream is still a SCAFFOLD — core does not expose /events and
  // nothing emits `campaign_updated`, so the subscription above never fires. This
  // panel lists RUNNING campaigns including paused ones, so a campaign the health
  // monitor auto-paused sits here looking supervised while it has stopped dialling.
  // Poll until the event actually exists.
  useRevalidate(loadRunning, { intervalMs: 30_000 });

  const utilisation =
    metrics.planUtilizationPct ??
    (metrics.planMinutesLimit
      ? Math.round((metrics.totalMinutes / metrics.planMinutesLimit) * 100)
      : 0);

  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl">
      <PageHeader
        title="Overview"
        subtitle={tenant?.companyName ? `${tenant.companyName} · this month` : 'This month'}
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner message={error} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          icon={Phone}
          label="Total calls"
          value={loading ? '…' : metrics.totalCalls?.toLocaleString() ?? '0'}
        />
        <KpiTile
          icon={Clock}
          label="Total minutes"
          value={loading ? '…' : metrics.totalMinutes?.toLocaleString() ?? '0'}
        />
        <KpiTile
          icon={Hash}
          label="Active phone numbers"
          value={loading ? '…' : metrics.activePhoneNumbers?.toLocaleString() ?? '0'}
        />
        <KpiTile
          icon={Gauge}
          label="Plan utilization"
          value={loading ? '…' : `${utilisation}%`}
          hint={
            metrics.planMinutesLimit
              ? `${metrics.totalMinutes ?? 0} / ${metrics.planMinutesLimit} minutes`
              : tenant?.plan
                ? `${tenant.plan} plan`
                : null
          }
        />
      </div>

      {/* What Alfred is about to DO, above what is merely running. Hides itself when
          nothing is scheduled, so it costs nothing on a quiet account. */}
      <div className="mt-8">
        {/* What is happening RIGHT NOW goes above what is happening later. */}
        <LiveCalls />
        {/* Two different mechanisms, one question. Scheduled campaign calls first —
            they are the volume; callbacks are the exceptions. */}
        <ScheduledCalls tenantWide title="Scheduled calls" />
        <UpcomingCalls />
      </div>

      {running.length > 0 && (
        <div className="mt-8">
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <Megaphone size={18} className="text-alfred-gold" />
              <h2 className="text-lg text-stone-100">Running campaigns</h2>
              <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">{running.length}</span>
            </div>
            <ul className="divide-y divide-stone-900">
              {running.map((c) => {
                const pct = c.totalContacts > 0
                  ? Math.round((c.contactsCompleted / c.totalContacts) * 100)
                  : 0;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/campaigns/${c.id}`)}
                      className="w-full flex items-center gap-3 py-3 text-left hover:opacity-80"
                    >
                      <span className={`badge ${STATUS_BADGE[c.status] || ''} text-[10px] shrink-0`}>{c.status}</span>
                      <AutoPauseChip campaign={c} />
                      <span className="min-w-0 flex-1 truncate text-stone-100">{c.name}</span>
                      <span className="shrink-0 text-xs text-stone-400 tabular-nums">
                        {pct}% · {c.contactsCompleted ?? 0}/{c.totalContacts ?? 0}
                      </span>
                      <ChevronRight size={16} className="shrink-0 text-stone-500" />
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-2 text-xs text-stone-500">Click a campaign to open it — pause, stop, or update it there.</p>
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="card">
          <h2 className="text-xl text-stone-100 mb-2">Welcome back</h2>
          <p className="text-sm text-stone-400">
            Use the navigation to view recent calls, inspect transcripts, or
            adjust your assistant's behaviour. Live updates from in-progress
            calls will appear here as they happen.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  // Owner switcher: reflect the tenant being VIEWED, not the login tenant — so the
  // exec-admin overview shows for a switched-into exec tenant and the label is right.
  const { tenant, selectedTenant, isOwner } = useAuth();
  const shownTenant = (isOwner && selectedTenant) || tenant;

  if (shownTenant?.features?.executiveAdmin) {
    return <ExecAdminOverview shownTenant={shownTenant} />;
  }
  // Use the tenant being VIEWED (shownTenant), not the login tenant — otherwise an
  // owner switching tenants keeps seeing their own tenant's name in the subtitle.
  return <StandardOverview tenant={shownTenant} />;
}
